// --- React & Redux
import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
// --- Hooks
import { useDialogsActions } from '../hooks';
import { useDialogsForm } from '../forms';
import { useLoading } from '../../../utils/hooks/useLoading';
import { useDeleteDialog } from './delete';
// --- API
import { datastageApi } from '../../../api';
// --- Actions
import { useProductsActions } from '../../../store/products/useProducts';
import { useContactsActions } from '../../../store/contacts/useContacts';
// --- UI Components
import Select from '@dtci/spellbook/Select';
import Typography from '@dtci/spellbook/Typography';
import { MultiSelect } from '@dtci/spellbook/MultiSelect';
import { Col, Row } from 'antd';
import { InputLabel } from '../../InputLabel/InputLabel';
import Input from '@dtci/spellbook/Input';
import Button from '@dtci/spellbook/Button';
// --- Utilities
import { filterCriteria } from '../../../utils/filterCriteria';
import { replaceObjectInArray, updateObjectValue } from '../../../utils/updateData';
import moment from 'moment';
import { useToastNotification } from '../../ToastNotifications';

/**
 * ContractualPartiesForm Component
 *
 * This component is responsible for rendering the form used to manage contractual parties
 * associated with a product. It allows users to add, update, and delete contractual parties,
 * as well as manage their details such as type and contract date.
 *
 * @component
 * @param {Object} data - The data object containing product details.
 * * @returns {JSX.Element} The rendered ContractualPartiesForm component.
 */
export const ContractualPartiesForm = ({ data }) => {
    const form = useDialogsForm();
    const { setLoadingTrue, setLoadingFalse } = useLoading();
    const [selectedContacts, setSelectedContacts] = useState([]);
    const { contractualParties, contractualPartyTypeOptions, contractualPartyTypes } = useSelector(
        (s) => s.contacts,
    );
    const { filterForm, productRange, productSort, cmContractualParties } = useSelector(
        (s) => s.products,
    );
    const { updateContacts } = useContactsActions();
    const { updateProducts } = useProductsActions();
    const { openDeleteDialog } = useDeleteDialog();
    const { openToast } = useToastNotification();

    const [savedContacts, setSavedContacts] = useState(cmContractualParties);

    // Utility to deep clone a contact (shallow for arrays, deep for objects)
    const cloneContact = (contact) => ({
        ...contact,
        contact: { ...contact.contact },
        contractualPartyType: contact.contractualPartyType
            ? { ...contact.contractualPartyType }
            : undefined,
    });

    // Function to load options for the MultiSelect component
    const loadOptions = async (inputValue, callback) => {
        // Return early if input length is 2 or less
        if (inputValue.length <= 2) {
            return callback([]);
        }

        try {
            // Fetch contacts from the API based on the input value
            const response = await datastageApi.contacts(
                { showOnlyWithRightslineIds: true, q: inputValue },
                [0, 9],
                ['id', 'DESC'],
            );

            // Map the response to create an array of contact options
            const contacts =
                response?.content?.map((x) => ({
                    label: x.name,
                    value: x.id,
                })) || [];

            // Update the state with the new contacts
            updateContacts({ contractualParties: contacts });

            // Call the callback function with the contacts
            callback(contacts);
        } catch (error) {
            console.error('Failed to load contact options:', error);
            callback([]);
        }
    };

    // Function to handle changes in the selected contact type
    const handleContactChange = (selectedType, contact, keyToUpdate) => {
        if (!selectedType || !contact || !keyToUpdate) return;

        const newPartyType = contractualPartyTypes.find((item) => item.id === selectedType.value);
        if (!newPartyType) return;

        // Deep clone the contact before updating
        const clonedContact = cloneContact(contact);
        clonedContact[keyToUpdate] = newPartyType;

        const updatedContacts = replaceObjectInArray(
            savedContacts,
            clonedContact.id,
            clonedContact,
        );

        setSavedContacts(updatedContacts);
        form.setValue('clearanceMemoContractualParties', updatedContacts);
    };

    // Function to handle adding selected contacts to the product
    const handleAddContact = async () => {
        setLoadingTrue();

        try {
            // Check if there are selected contacts to add.
            if (selectedContacts.length === 0) {
                setLoadingFalse();
                return;
            }

            // Add the selected contacts to the product using the API
            const res = await datastageApi.addContractualParties(data.productId, selectedContacts);

            if (res?.status === 200) {
                // Fetch the updated product data after adding the contacts.
                const updatedProducts = await datastageApi.products(
                    filterCriteria(filterForm, '', 'products'),
                    productRange,
                    productSort,
                );

                // Update the products state with the new data
                updateProducts({
                    products: updatedProducts?.content,
                    totalPages: updatedProducts?.totalPages,
                    totalItems: updatedProducts?.totalElements,
                    currentPage: updatedProducts?.pageable?.pageNumber,
                });

                const selectedProduct = updatedProducts?.content?.find(
                    (product) => product.productId === data.productId
                );

                // Update the form value for 'clearanceMemoContractualParties' with the updated contacts
                setSavedContacts(
                    selectedProduct?.clearanceMemoContractualParties || [],
                );

                // Clear the selected contacts after adding them
                setSelectedContacts([]);

                openToast('Contractual Parties added successfully', 'success');
            } else {
                openToast(
                    `${
                        (res?.statusText && res?.statusText) || 'Failed to add Contractual Parties'
                    }`,
                    'error',
                );
            }
        } catch (error) {
            console.error('Error adding contractual parties:', error);
            openToast('Failed to add Contractual Parties', 'error');
        } finally {
            setLoadingFalse();
        }
    };

    // Function to handle changes in the contract date
    const handleDateChange = (contractDate, contact, keyToUpdate) => {
        const newDate = new Date(contractDate);

        // Deep clone the contact before updating
        const clonedContact = cloneContact(contact);
        clonedContact[keyToUpdate] = newDate;

        const updatedContacts = replaceObjectInArray(
            savedContacts,
            clonedContact.id,
            clonedContact,
        );

        setSavedContacts(updatedContacts);
        form.setValue('clearanceMemoContractualParties', updatedContacts);
    };

    // Function to handle deleting a contact
    const handleDeleteContact = (contact) => {
        // Exit if no contact is provided
        openDeleteDialog(
            'Yes, Delete',
            [
                `Are you sure you want to remove ${contact?.contact?.name} from this product?`,
                'Please note: This action can not be reversed.',
            ],
            async () => {
                setLoadingTrue();
                try {
                    // Delete the contact using the API
                    const res = await datastageApi.deleteContractualParty(contact?.id);

                    if (res?.status === 200) {
                        setSavedContacts((prevContacts) => {
                            // Filter out the deleted contact from the saved contacts
                            const filteredContacts = prevContacts.filter(
                                (obj) => obj.id !== contact.id,
                            );

                            // Update the saved contacts state with the filtered contacts
                            form.setValue('clearanceMemoContractualParties', filteredContacts);
                            return filteredContacts;
                        });

                        openToast('Contractual Party removed successfully', 'success');
                    } else {
                        openToast(
                            `${
                                (res?.statusText && res?.statusText) ||
                                'Failed to remove Contractual Party'
                            }`,
                            'error',
                        );
                    }
                } catch (error) {
                    console.error('Error deleting contact:', error);
                    openToast('Failed to remove Contractual Party', 'error');
                } finally {
                    setLoadingFalse();
                }
            },
        );
    };

    return (
        <div className="flexBox">
            {/* Display the comment title along with the product ID */}
            <Typography variant="T20" bold>
                {data.title} ({data.productId})
            </Typography>

            {/* Row for selecting contractual parties */}
            <Row>
                <MultiSelect
                    async
                    name="contractualParty"
                    defaultOptions={contractualParties}
                    loadOptions={loadOptions}
                    useMenuPortal
                    isSearchable
                    isClearable
                    value={selectedContacts}
                    onChange={setSelectedContacts} // Using the setter directly
                />
                {/* Button to add selected contacts */}
                <Button
                    iconType="fa-regular fa-plus"
                    sizeType="small"
                    styleType="link"
                    onClick={handleAddContact}
                    disabled={selectedContacts.length === 0}
                >
                    Add Contacts
                </Button>
            </Row>

            <br />

            {/* Display message if no contractual parties are associated yet */}
            {savedContacts.length === 0 ? (
                <Typography variant="T20">No Contractual Parties Associated Yet</Typography>
            ) : (
                savedContacts.map((contact) => (
                    <Row key={contact.id}>
                        {/* Display contact information */}
                        <Col span={7} style={{ marginRight: 20 }}>
                            <InputLabel label="Contact">
                                <Select
                                    name="type"
                                    isDisabled
                                    value={{
                                        label: contact?.contact?.name,
                                        value: contact?.contact?.id,
                                    }}
                                />
                            </InputLabel>
                        </Col>

                        {/* Select for contractual party type */}
                        <Col span={7} style={{ marginRight: 20 }}>
                            <InputLabel label="Contractual Party Type">
                                <Select
                                    name="type"
                                    isSearchable
                                    useMenuPortal
                                    options={contractualPartyTypeOptions}
                                    value={contractualPartyTypeOptions.find(
                                        (item) => item.value === contact?.contractualPartyType?.id,
                                    )}
                                    onChange={(e) =>
                                        handleContactChange(e, contact, 'contractualPartyType')
                                    }
                                />
                            </InputLabel>
                        </Col>

                        {/* Input field for contract date */}
                        <Col span={7}>
                            <InputLabel label="Contract Date">
                                <Input
                                    labelText="Date Input"
                                    type="date"
                                    value={moment(contact?.contractDate).format('YYYY-MM-DD')}
                                    onChange={(e) =>
                                        handleDateChange(e.target.value, contact, 'contractDate')
                                    }
                                />
                            </InputLabel>
                        </Col>

                        {/* Button to delete the contact */}
                        <Button
                            style={{ marginTop: 20 }}
                            iconType="fa-regular fa-trash"
                            className="filter-button"
                            sizeType="small"
                            styleType="link"
                            onClick={() => handleDeleteContact(contact)}
                        />
                    </Row>
                ))
            )}
        </div>
    );
};

/**
 * useContractualPartiesDialog Hook
 *
 * This hook provides functionality to open the contractual parties' dialog.
 * It uses the useDialogsActions and useDialogsForm hooks to manage the dialog state.
 * It also provides a handleSubmit function to handle the form submission.
 *
 * @returns {Object} An object containing the openContractualPartiesDialog function.
 */
export const useContractualPartiesDialog = () => {
    const { openDialog, closeDialog } = useDialogsActions();
    const form = useDialogsForm();

    // Function to handle form submission
    const handleSubmit = useCallback(
        (onSubmit) => () => {
            onSubmit(form.state.formData);
            form.reset();
            closeDialog();
        },
        [form, closeDialog],
    );

    // Function to open the contractual parties dialog
    const openContractualPartiesDialog = useCallback(
        // Open the dialog with the provided data
        (data, onConfirm) =>
            openDialog({
                title: 'Clearance Memo Contractual Parties',
                content: <ContractualPartiesForm data={data} />,
                closeAction: true,
                primaryAction: {
                    label: 'Save',
                    action: handleSubmit(onConfirm),
                },
            }),
        [openDialog, handleSubmit],
    );

    return { openContractualPartiesDialog };
};
